import React, { useRef } from "react";
import { useNode } from "@craftjs/core";
import "../../styles/components/user/UserComponents.css";
import "../../styles/landing.css";

interface ContainerProps {
  background?: string;
  padding?: string;
  margin?: string;
  borderRadius?: number;
  width?: string;
  height?: string;
  flexDirection?: string;
  alignItems?: string;
  justifyContent?: string;
  flexWrap?: string;
  border?: string;
  gap?: string;
  boxShadow?: string;
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties; // Add style property
  onClick?: () => void; // Add onClick property
}

export const Container = ({
  background = "#ffffff",
  padding = "20px",
  margin = "0",
  borderRadius = 0,
  width = "100%",
  height = "auto",
  flexDirection = "column",
  alignItems = "center",
  justifyContent = "flex-start",
  flexWrap = "nowrap",
  border,
  gap,
  boxShadow,
  children,
  className,
  style, // Add style to props destructuring
  onClick, // Add onClick to props destructuring
}: ContainerProps) => {
  const {
    connectors: { connect, drag },
    selected,
    actions: { setProp },
  } = useNode((node) => ({
    selected: node.events.selected,
  }));

  // Reference for resize handles
  const resizeHandleRef = useRef<HTMLDivElement>(null);

  const handleResize = (e: React.MouseEvent, direction: string) => {
    e.stopPropagation();
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = parseInt(width);
    const startHeight = parseInt(height);

    const onMouseMove = (moveEvent: MouseEvent) => {
      moveEvent.stopPropagation();

      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;

      if (direction === "right" || direction === "bottom-right") {
        const newWidth = Math.max(50, startWidth + deltaX);
        setProp((props: ContainerProps) => (props.width = `${newWidth}px`));
      }

      if (direction === "bottom" || direction === "bottom-right") {
        const newHeight = Math.max(50, startHeight + deltaY);
        setProp((props: ContainerProps) => (props.height = `${newHeight}px`));
      }
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };

  // Create a ref that will be passed to the connect and drag functions
  const containerRef = useRef<HTMLDivElement>(null);

  // Connect the ref to both connect and drag functions
  React.useEffect(() => {
    if (containerRef.current) {
      connect(containerRef.current);
      drag(containerRef.current);
    }
  }, [connect, drag]);

  return (
    <div
      ref={containerRef}
      className={`container-component ${className || ""} ${
        selected ? "selected-component" : ""
      }`}
      style={{
        background,
        padding,
        margin,
        borderRadius: `${borderRadius}px`,
        width,
        height,
        display: "flex",
        flexDirection: flexDirection as "column" | "row",
        justifyContent,
        alignItems,
        flexWrap: flexWrap as "nowrap" | "wrap" | "wrap-reverse",
        outline: selected ? "2px solid #0f3460" : "none",
        boxShadow: selected ? "0 0 0 1px #0f3460" : boxShadow,
        position: "relative",
        border,
        gap,
        cursor: onClick ? "pointer" : "default",
        ...style, // Add spread of additional styles
      }}
      onClick={(e) => {
        e.stopPropagation();
        // Execute onClick if provided
        if (onClick) onClick();
      }}
    >
      {selected && (
        <div
          className="component-selected-indicator"
          style={{
            position: "absolute",
            top: "-16px",
            left: "0",
            background: "#0f3460",
            color: "white",
            fontSize: "10px",
            padding: "2px 4px",
            borderRadius: "3px 3px 0 0",
            zIndex: 10,
            pointerEvents: "none",
          }}
        >
          Container
        </div>
      )}
      {children}

      {selected && (
        <>
          {/* Resize handle - bottom right corner */}
          <div
            className="resize-handle resize-handle-br"
            ref={resizeHandleRef}
            onMouseDown={(e) => handleResize(e, "bottom-right")}
            style={{
              position: "absolute",
              width: "10px",
              height: "10px",
              background: "#0f3460",
              border: "2px solid white",
              borderRadius: "50%",
              bottom: "-5px",
              right: "-5px",
              cursor: "nwse-resize",
              zIndex: 999,
            }}
          />

          {/* Resize handle - right side */}
          <div
            className="resize-handle resize-handle-right"
            onMouseDown={(e) => handleResize(e, "right")}
            style={{
              position: "absolute",
              width: "10px",
              height: "10px",
              background: "#0f3460",
              border: "2px solid white",
              borderRadius: "50%",
              top: "calc(50% - 5px)",
              right: "-5px",
              cursor: "ew-resize",
              zIndex: 999,
            }}
          />

          {/* Resize handle - bottom side */}
          <div
            className="resize-handle resize-handle-bottom"
            onMouseDown={(e) => handleResize(e, "bottom")}
            style={{
              position: "absolute",
              width: "10px",
              height: "10px",
              background: "#0f3460",
              border: "2px solid white",
              borderRadius: "50%",
              bottom: "-5px",
              left: "calc(50% - 5px)",
              cursor: "ns-resize",
              zIndex: 999,
            }}
          />
        </>
      )}
    </div>
  );
};

export const ContainerSettings = () => {
  const {
    actions: { setProp },
    props,
  } = useNode((node) => ({
    props: node.data.props || {},
  }));

  return (
    <div className="settings-container">
      <h4>Container Settings</h4>
      <div className="form-group">
        <label>Background</label>
        <input
          type="color"
          className="form-control color-picker"
          value={
            props.background && props.background.startsWith("#")
              ? props.background
              : "#ffffff"
          }
          onChange={(e) =>
            setProp(
              (props: ContainerProps) => (props.background = e.target.value)
            )
          }
        />
        <input
          type="text"
          className="form-control"
          value={props.background || ""}
          onChange={(e) =>
            setProp(
              (props: ContainerProps) => (props.background = e.target.value)
            )
          }
          placeholder="e.g. #ffffff, rgba(0,0,0,0.5), url(...)"
          style={{ marginTop: "5px" }}
        />
      </div>
      <div className="form-group">
        <label>Width</label>
        <input
          type="text"
          className="form-control"
          value={props.width || ""}
          onChange={(e) =>
            setProp((props: ContainerProps) => (props.width = e.target.value))
          }
          placeholder="e.g. 100%, 300px"
        />
      </div>
      <div className="form-group">
        <label>Height</label>
        <input
          type="text"
          className="form-control"
          value={props.height || ""}
          onChange={(e) =>
            setProp((props: ContainerProps) => (props.height = e.target.value))
          }
          placeholder="e.g. auto, 300px"
        />
      </div>
      <div className="form-group">
        <label>Padding</label>
        <input
          type="text"
          className="form-control"
          value={props.padding || ""}
          onChange={(e) =>
            setProp((props: ContainerProps) => (props.padding = e.target.value))
          }
          placeholder="e.g. 10px"
        />
      </div>
      <div className="form-group">
        <label>Margin</label>
        <input
          type="text"
          className="form-control"
          value={props.margin || ""}
          onChange={(e) =>
            setProp((props: ContainerProps) => (props.margin = e.target.value))
          }
          placeholder="e.g. 10px 0"
        />
      </div>
      <div className="form-group">
        <label>Border Radius (px)</label>
        <input
          type="number"
          className="form-control"
          value={props.borderRadius || 0}
          onChange={(e) =>
            setProp(
              (props: ContainerProps) =>
                (props.borderRadius = parseInt(e.target.value))
            )
          }
        />
      </div>
      <div className="form-group">
        <label>Flex Direction</label>
        <select
          className="form-control"
          value={props.flexDirection || "column"}
          onChange={(e) =>
            setProp(
              (props: ContainerProps) => (props.flexDirection = e.target.value)
            )
          }
        >
          <option value="row">Row</option>
          <option value="column">Column</option>
        </select>
      </div>
      <div className="form-group">
        <label>Flex Wrap</label>
        <select
          className="form-control"
          value={props.flexWrap || "nowrap"}
          onChange={(e) =>
            setProp(
              (props: ContainerProps) => (props.flexWrap = e.target.value)
            )
          }
        >
          <option value="nowrap">No Wrap</option>
          <option value="wrap">Wrap</option>
          <option value="wrap-reverse">Wrap Reverse</option>
        </select>
      </div>
      <div className="form-group">
        <label>Align Items</label>
        <select
          className="form-control"
          value={props.alignItems || "center"}
          onChange={(e) =>
            setProp(
              (props: ContainerProps) => (props.alignItems = e.target.value)
            )
          }
        >
          <option value="flex-start">Start</option>
          <option value="center">Center</option>
          <option value="flex-end">End</option>
          <option value="stretch">Stretch</option>
        </select>
      </div>
      <div className="form-group">
        <label>Justify Content</label>
        <select
          className="form-control"
          value={props.justifyContent || "flex-start"}
          onChange={(e) =>
            setProp(
              (props: ContainerProps) => (props.justifyContent = e.target.value)
            )
          }
        >
          <option value="flex-start">Start</option>
          <option value="center">Center</option>
          <option value="flex-end">End</option>
          <option value="space-between">Space Between</option>
          <option value="space-around">Space Around</option>
        </select>
      </div>
      <div className="form-group">
        <label>Border</label>
        <input
          type="text"
          className="form-control"
          value={props.border || ""}
          onChange={(e) =>
            setProp((props: ContainerProps) => (props.border = e.target.value))
          }
          placeholder="e.g. 1px solid #000"
        />
      </div>
      <div className="form-group">
        <label>Gap</label>
        <input
          type="text"
          className="form-control"
          value={props.gap || ""}
          onChange={(e) =>
            setProp((props: ContainerProps) => (props.gap = e.target.value))
          }
          placeholder="e.g. 10px or 10px 20px"
        />
      </div>
      <div className="form-group">
        <label>Box Shadow</label>
        <input
          type="text"
          className="form-control"
          value={props.boxShadow || ""}
          onChange={(e) =>
            setProp(
              (props: ContainerProps) => (props.boxShadow = e.target.value)
            )
          }
          placeholder="e.g. 0 4px 8px rgba(0,0,0,0.1)"
        />
      </div>
    </div>
  );
};

Container.craft = {
  props: {
    background: "#ffffff",
    padding: "20px",
    margin: "0",
    borderRadius: 0,
    width: "100%",
    height: "auto",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    flexWrap: "nowrap",
    border: "",
    gap: "",
    boxShadow: "",
    style: undefined, // Add style to craft props
    onClick: undefined, // Add onClick to craft props
  },
  related: {
    settings: ContainerSettings,
  },
};
